export interface DeckCard {
  quantity: number;
  name: string;
  setCode: string;
  cardNumber: string;
  raw: string;
}

export interface DeckParseResult {
  cards: DeckCard[];
  deckName: string;
  totalCards: number;
}

const HEADER = "// PokemonCard.io Deck List";

// Matches: {qty} {name} {setCode}-{number[suffix]}
// setCode is alphanumeric (e.g. sv3pt5, swsh10, me2pt5); number is digits + optional letter
const LINE_RE = /^(\d+)\s+(.+?)\s+([\w]+-\d+[a-z]*)$/i;

export function isPokemonCardIoFormat(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.startsWith(HEADER)) return true;

  // If majority of non-comment, non-empty lines match the deck-line pattern, treat as pokemoncard.io
  const lines = trimmed
    .split(/\r?\n/)
    .filter((l) => l.trim() && !l.trim().startsWith("//"));
  if (lines.length === 0) return false;
  const matchCount = lines.filter((l) => LINE_RE.test(l.trim())).length;
  // At least 1 matching line and ≥60% hit rate (no minimum length gate)
  return matchCount >= 1 && matchCount / lines.length >= 0.6;
}

export function parsePokemonCardIoDeckList(text: string): DeckParseResult {
  const lines = text.trim().split(/\r?\n/);
  let deckName = "Imported Deck";
  const cards: DeckCard[] = [];

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    if (line.startsWith("// Name:")) {
      const parsed = line.replace(/^\/\/\s*Name:\s*/i, "").trim();
      if (parsed) deckName = parsed;
      continue;
    }
    if (line.startsWith("//")) continue;

    const match = line.match(LINE_RE);
    if (!match) continue;

    const quantity = parseInt(match[1], 10);
    const name = match[2].trim();
    const token = match[3];

    // Split at the last hyphen to separate setCode from card number
    const lastHyphen = token.lastIndexOf("-");
    const setCode = token.slice(0, lastHyphen);
    const cardNumber = token.slice(lastHyphen + 1);

    cards.push({ quantity, name, setCode, cardNumber, raw: line });
  }

  const totalCards = cards.reduce((s, c) => s + c.quantity, 0);
  return { cards, deckName, totalCards };
}

// ── Deck search / fetch client ──────────────────────────────────────────────
// TNK-43 — server proxy at /api/pokemon-card/{search,deck} performs the
// actual scrape (CORS + IP-reputation reasons) and enforces 10/min search +
// 30/min deck-fetch rate limits per operator IP. These wrappers just call
// the proxy + unwrap the JSON.

import { apiFetch } from "@/lib/apiClient";

export interface DeckSearchResult {
  title: string;
  slug: string;
  url: string;
  format?: string;
  price?: string;
  author?: string;
  views?: string;
}

async function errorFromResponse(
  res: Response,
  fallback: string,
): Promise<Error> {
  try {
    const body = (await res.json()) as { error?: string };
    return new Error(body.error ?? fallback);
  } catch {
    return new Error(fallback);
  }
}

export async function searchDecks(query: string): Promise<DeckSearchResult[]> {
  const res = await apiFetch(
    `/api/pokemon-card/search?q=${encodeURIComponent(query)}`,
  );
  if (!res.ok) {
    throw await errorFromResponse(
      res,
      res.status === 429
        ? "Too many searches — wait a minute and try again."
        : "Search failed. Please try again.",
    );
  }
  const json = (await res.json()) as { results: DeckSearchResult[] };
  return json.results;
}

export async function fetchDeckCards(deckUrl: string): Promise<DeckCard[]> {
  const res = await apiFetch(
    `/api/pokemon-card/deck?url=${encodeURIComponent(deckUrl)}`,
  );
  if (!res.ok) {
    throw await errorFromResponse(
      res,
      res.status === 429
        ? "Too many deck loads — wait a minute and try again."
        : "Could not load the deck.",
    );
  }
  const json = (await res.json()) as { cards: DeckCard[] };
  return json.cards;
}
